import React from "react";
import { Link } from "react-router-dom";
import { useInView } from "react-intersection-observer";
import {
  FiArrowLeft,
  FiArrowRight,
  FiCheckCircle,
  FiCoffee,
  FiExternalLink,
  FiMessageCircle,
  FiMonitor,
  FiUsers,
} from "react-icons/fi";
import {
  typography,
  spacing,
  components,
  gradients,
} from "../styles/designSystem";
import { testimonials } from "../data/testimonialsData";

const websiteGoals = [
  {
    icon: FiCoffee,
    eyebrow: "Product showcase",
    title: "Keep the drinks at the center",
    text: "Large product images and simple category controls make the menu easy to understand at a glance.",
  },
  {
    icon: FiMessageCircle,
    eyebrow: "Direct conversion",
    title: "Move naturally into WhatsApp",
    text: "Prepared order messages give customers a familiar next step without adding an unnecessary checkout system.",
  },
  {
    icon: FiUsers,
    eyebrow: "Business enquiries",
    title: "Support events and catering",
    text: "The same website also introduces Twizel to event organizers, partners, and customers planning larger orders.",
  },
];

const projectScreenshots = [
  {
    src: "/images/twizel/twizel-home.png",
    fallback: "/images/twizel/twizel-preview.png",
    alt: "Twizel homepage",
    title: "Homepage",
    description: "A clear introduction to the brand and its featured products.",
  },
  {
    src: "/images/twizel/twizel-menu.png",
    fallback: "/images/twizel/twizel-preview.png",
    alt: "Twizel menu",
    title: "Menu",
    description: "A visual browsing experience designed around the drinks.",
  },
  {
    src: "/images/twizel/twizel-occasions.png",
    fallback: "/images/twizel/twizel-preview.png",
    alt: "Twizel occasions section",
    title: "Occasions",
    description: "A simple path for catering and event enquiries.",
  },
];

const technologies = [
  "React",
  "Tailwind CSS",
  "Responsive design",
  "WhatsApp integration",
  "Touch interactions",
];

const ScreenshotImage = ({ src, fallback, alt, className }) => {
  const handleError = (event) => {
    if (!fallback || event.currentTarget.dataset.fallbackApplied === "true") {
      return;
    }

    event.currentTarget.dataset.fallbackApplied = "true";
    event.currentTarget.src = fallback;
  };

  return (
    <img src={src} alt={alt} onError={handleError} className={className} />
  );
};

const TwizelProjectPage = () => {
  const { ref: heroRef, inView: heroInView } = useInView({
    triggerOnce: true,
    threshold: 0.15,
  });

  const { ref: goalsRef, inView: goalsInView } = useInView({
    triggerOnce: true,
    threshold: 0.12,
  });

  const { ref: galleryRef, inView: galleryInView } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const projectTestimonial = testimonials.find(
    (testimonial) => testimonial.id === "twizel",
  );

  const reveal = (visible) =>
    `transition-all duration-700 ease-out ${
      visible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"
    }`;

  return (
    <main className="relative min-h-screen overflow-hidden bg-white pt-24 md:pt-32">
      <style>{`
        @keyframes twizelFloat {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-12px); }
        }

        @keyframes twizelDrift {
          0%, 100% { transform: translate(0, 0); }
          50% { transform: translate(14px, -8px); }
        }

        .twizel-float {
          animation: twizelFloat 7s ease-in-out infinite;
        }

        .twizel-drift {
          animation: twizelDrift 9s ease-in-out infinite;
        }

        @media (prefers-reduced-motion: reduce) {
          .twizel-float,
          .twizel-drift {
            animation: none;
          }
        }
      `}</style>

      <div className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-white via-white to-primary/[0.035]" />
        <div className="twizel-drift absolute -right-24 top-20 h-72 w-72 rounded-full bg-primary/[0.08] blur-3xl" />
        <div className="absolute -left-24 top-[48%] h-52 w-52 rounded-[3rem] border border-primary/10 bg-primary/[0.025]" />
        <div className="absolute bottom-24 right-[10%] h-24 w-24 rotate-12 rounded-3xl border border-secondary/10" />
      </div>

      <section
        className={`${spacing.container} ${spacing.section} relative z-10`}
      >
        <Link
          to="/projects"
          className="mb-12 inline-flex items-center gap-2 text-sm font-medium text-gray-600 transition-colors hover:text-primary md:mb-16"
        >
          <FiArrowLeft />
          Back to projects
        </Link>

        {/* Hero */}
        <section
          ref={heroRef}
          className="grid items-center gap-14 lg:grid-cols-[0.82fr,1.18fr] lg:gap-20"
        >
          <div className={reveal(heroInView)}>
            <p className={`${typography.label} text-primary`}>
              Product Showcase Website
            </p>

            <h1 className={`${typography.heading.h1} mt-5 text-secondary`}>
              TWIZEL
            </h1>

            <p className={`${typography.body.large} mt-6 max-w-xl`}>
              A responsive website for a Surabaya drink brand, created to
              showcase its products, guide customers toward WhatsApp orders, and
              introduce its event and catering services.
            </p>

            <div className="mt-7 flex flex-wrap gap-2">
              {["Product-led design", "Mobile-first", "WhatsApp ordering"].map(
                (item) => (
                  <span
                    key={item}
                    className="rounded-full border border-primary/15 bg-primary/[0.06] px-3 py-1.5 text-xs font-medium text-primary"
                  >
                    {item}
                  </span>
                ),
              )}
            </div>

            <div className="mt-8 space-y-3">
              {[
                "Interactive menu browsing",
                "Clear preorder information",
                "Dedicated event enquiries",
              ].map((item) => (
                <div
                  key={item}
                  className="flex items-center gap-3 text-sm font-medium text-secondary"
                >
                  <FiCheckCircle className="shrink-0 text-primary" />
                  {item}
                </div>
              ))}
            </div>

            <div className="mt-9 flex flex-wrap gap-3">
              <a
                href="https://www.twizel.id"
                target="_blank"
                rel="noreferrer"
                className={components.button.primary}
              >
                Visit Twizel
                <FiExternalLink />
              </a>
            </div>
          </div>

          <div
            className={`${reveal(heroInView)} relative`}
            style={{ transitionDelay: "140ms" }}
          >
            <div className="twizel-float absolute -right-5 -top-7 h-20 w-20 rounded-[1.6rem] bg-primary/10" />
            <div className="absolute -bottom-8 -left-8 h-28 w-28 rounded-full border-[10px] border-primary/[0.08]" />

            <div
              className={`relative overflow-hidden rounded-[2rem] border border-gray-200/70 p-3 shadow-2xl ${gradients.subtle}`}
            >
              <div className="overflow-hidden rounded-[1.35rem] bg-white">
                <div className="flex items-center gap-2 border-b border-gray-100 bg-gray-50 px-4 py-3">
                  <span className="h-3 w-3 rounded-full bg-gray-300" />
                  <span className="h-3 w-3 rounded-full bg-gray-300" />
                  <span className="h-3 w-3 rounded-full bg-gray-300" />
                  <span className="ml-3 text-xs text-gray-500">twizel.id</span>
                </div>

                <ScreenshotImage
                  src="/images/twizel/twizel-preview.png"
                  fallback="/images/drinks/sticky-milk-thai-tea.png"
                  alt="Twizel website preview"
                  className="h-[360px] w-full object-cover object-top md:h-[540px]"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Main website goals */}
        <section ref={goalsRef} className="mt-24 md:mt-32">
          <div className={reveal(goalsInView)}>
            <p className={`${typography.label} text-primary`}>
              What the website does
            </p>

            <h2
              className={`mt-4 ${typography.heading.h2} max-w-2xl text-secondary`}
            >
              Three clear purposes, without unnecessary complexity.
            </h2>
          </div>

          <div className="mt-10 grid gap-5 md:grid-cols-3">
            {websiteGoals.map((goal, index) => {
              const Icon = goal.icon;

              return (
                <article
                  key={goal.title}
                  className={`${components.card.base} group relative overflow-hidden p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl`}
                  style={{ transitionDelay: `${index * 80}ms` }}
                >
                  <div className="absolute -right-10 -top-10 h-28 w-28 rounded-full bg-primary/[0.05] transition-transform duration-500 group-hover:scale-150" />

                  <div className="relative">
                    <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                      <Icon size={20} />
                    </div>

                    <p className={`${typography.label} mt-6 text-primary`}>
                      {goal.eyebrow}
                    </p>

                    <h3 className="mt-3 text-xl font-semibold text-secondary">
                      {goal.title}
                    </h3>

                    <p
                      className={`${typography.body.small} mt-3 text-gray-600`}
                    >
                      {goal.text}
                    </p>
                  </div>
                </article>
              );
            })}
          </div>
        </section>

        {/* Compact editorial gallery */}
        <section ref={galleryRef} className="mt-24 md:mt-32">
          <div className={reveal(galleryInView)}>
            <p className={`${typography.label} text-primary`}>
              Website preview
            </p>
            <h2 className={`${typography.heading.h2} mt-4 text-secondary`}>
              Selected screens from the final experience.
            </h2>
          </div>

          <div className="mt-10 grid gap-6 lg:grid-cols-[1.35fr,0.65fr]">
            <article className="group overflow-hidden rounded-[2rem] border border-gray-200/70 bg-white p-3 shadow-lg transition-all duration-300 hover:-translate-y-1 hover:shadow-xl">
              <div className="overflow-hidden rounded-[1.4rem] bg-gray-100">
                <ScreenshotImage
                  src={projectScreenshots[0].src}
                  fallback={projectScreenshots[0].fallback}
                  alt={projectScreenshots[0].alt}
                  className="h-[390px] w-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.02] md:h-[560px]"
                />
              </div>

              <div className="px-3 pb-4 pt-5">
                <h3 className="text-lg font-semibold text-secondary">
                  {projectScreenshots[0].title}
                </h3>
                <p className="mt-2 text-sm leading-6 text-gray-600">
                  {projectScreenshots[0].description}
                </p>
              </div>
            </article>

            <div className="grid gap-6">
              {projectScreenshots.slice(1).map((screenshot) => (
                <article
                  key={screenshot.src}
                  className="group overflow-hidden rounded-[2rem] border border-gray-200/70 bg-white p-3 shadow-lg transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
                >
                  <div className="overflow-hidden rounded-[1.4rem] bg-gray-100">
                    <ScreenshotImage
                      src={screenshot.src}
                      fallback={screenshot.fallback}
                      alt={screenshot.alt}
                      className="h-[220px] w-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.02] md:h-[250px]"
                    />
                  </div>

                  <div className="px-3 pb-3 pt-4">
                    <h3 className="text-base font-semibold text-secondary">
                      {screenshot.title}
                    </h3>
                    <p className="mt-1.5 text-sm leading-6 text-gray-600">
                      {screenshot.description}
                    </p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonial */}
        {projectTestimonial && (
          <section className="mt-24 md:mt-32">
            <div className="relative overflow-hidden rounded-[2rem] border border-primary/10 bg-primary/[0.045] p-8 md:p-11">
              <div className="pointer-events-none absolute -right-14 -top-14 h-44 w-44 rounded-full bg-primary/[0.07] blur-2xl" />

              <div className="relative mx-auto max-w-3xl">
                <div
                  className="text-5xl leading-none text-primary"
                  aria-hidden="true"
                >
                  &ldquo;
                </div>

                <blockquote
                  className={`${typography.body.base} mt-5 italic leading-8 text-gray-700`}
                >
                  {projectTestimonial.quote}
                </blockquote>

                <div className="mt-7 flex items-center gap-4 border-t border-primary/10 pt-6">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-primary text-sm font-bold text-white shadow-md">
                    {projectTestimonial.initials}
                  </div>

                  <div>
                    <p className="text-sm font-semibold text-secondary">
                      {projectTestimonial.name}
                    </p>
                    <p className={`${typography.body.xs} text-gray-500`}>
                      {projectTestimonial.role}
                      {projectTestimonial.company
                        ? `, ${projectTestimonial.company}`
                        : ""}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Technology + CTA */}
        <section className="mt-24 pb-8 text-center md:mt-32">
          <div className="mx-auto max-w-2xl">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary">
              <FiMonitor size={22} />
            </div>

            <p className={`${typography.label} mt-6 text-primary`}>
              Technology
            </p>

            <div className="mt-5 flex flex-wrap justify-center gap-2">
              {technologies.map((technology) => (
                <span
                  key={technology}
                  className="rounded-full bg-gray-100 px-3 py-1.5 text-xs font-medium text-gray-700"
                >
                  {technology}
                </span>
              ))}
            </div>

            <h2 className={`${typography.heading.h2} mt-12 text-secondary`}>
              Need a website that presents your products clearly?
            </h2>

            <p className={`${typography.body.base} mx-auto mt-5 max-w-xl`}>
              We create focused digital experiences that give products a strong
              presence and customers an obvious next step.
            </p>

            <Link
              to="/contact"
              className={`${components.button.primary} mx-auto mt-9`}
            >
              Get in touch
              <FiArrowRight />
            </Link>
          </div>
        </section>
      </section>
    </main>
  );
};

export default TwizelProjectPage;
